import BaseModal from "../../../../components/ui/BaseModal";
import BaseTextField from "../../../../components/ui/BaseTextField";
import BaseButton from "../../../../components/ui/BaseButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../../store/toast/toastSlice";
import {
  createCompanyBranch,
  updateBranch,
} from "../../../../services/api/branch";
import PhoneInput from "react-phone-input-2";
import { FormHelperText } from "@mui/material";
import BaseAutoSelect from "../../../../components/ui/BaseAutoSelect";
const getValidationSchema = (isEdit) =>
  yup.object({
    name: yup.string("").required("Enter First name"),
    phone: yup.string("Enter phone number").required("Enter phone number"),
    email: yup
      .string()
      .required("Email is required")
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Invalid email format"
      ),
    country: yup.string("").required("Enter Country"),
    city: yup.string("").required("Enter City"),
    addressLine1: yup.string("").required("Enter Address"),
    zipCode: yup.string("").required("Enter Zip code"),
    state: yup.string("").required("Enter State"),
    taxType: yup.string("").required("Select Tax Type"),
    taxNumber: yup.string("").required("Enter number"),
  });

const AddEditCompanyBranchModal = ({
  open,
  close,
  setCompanyBranchesList,
  selectedBranch,
  setSelectedBranch,
  isEditMode = true,
  setIsEditMode,
  refetchBranchesData,
}) => {
  const dispatch = useDispatch();
  const {
    isPending: isCreateCompanyBranchApiLoading,
    error: isCreateCompanyBranchApiError,
    data: newBranchData,
    mutateAsync: createCompanyBranchMutation,
  } = useMutation({
    mutationFn: createCompanyBranch,
    enabled: false,
  });

  const {
    isPending: isUpdateBranchApiLoading,
    error: isUpdateBranchApiError,
    data: updatedBranch,
    mutateAsync: updateBranchMutation,
  } = useMutation({
    mutationFn: updateBranch,
    enabled: false,
  });

  // Opened from the ID / name cell -> read-only, same as the bank account modal.
  const isReadOnly = !!selectedBranch && !isEditMode;

  const formik = useFormik({
    initialValues: {
      name: selectedBranch?.name,
      phone: selectedBranch?.phone,
      email: selectedBranch?.email,
      country: selectedBranch?.address?.country || "",
      addressLine1: selectedBranch?.address?.addressLine1 || "",
      city: selectedBranch?.address?.city || "",
      state: selectedBranch?.address?.state || "",
      zipCode: selectedBranch?.address?.zipCode || "",
      taxType: selectedBranch?.taxType || "",
      taxNumber: selectedBranch?.taxNumber || "",
    },
    validationSchema: getValidationSchema(!!selectedBranch),
    onSubmit: (values) => {
      handleSubmitButton();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleSubmitButton = () => {
    if (selectedBranch) {
      editBranch();
    } else {
      createNewBranch();
    }
  };

  const createNewBranch = async () => {
    try {
      const newBranch = await createCompanyBranchMutation(formik.values);
      setCompanyBranchesList((prevBranches) => [
        newBranch?.data,
        ...prevBranches,
      ]);
      dispatch(
        showToast({
          message: "Branch created successfully",
          type: "success",
        })
      );
      refetchBranchesData();
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        })
      );
    }
  };

  const editBranch = async () => {
    try {
      const payload = {
        id: selectedBranch?.id,
        ...formik.values,
      };
      const newBranch = await updateBranchMutation(payload);
      setCompanyBranchesList((prevBranches) =>
        prevBranches.map((branch) =>
          branch.id === newBranch?.data?.id
            ? { ...branch, ...newBranch?.data }
            : branch
        )
      );

      dispatch(
        showToast({
          message: "Branch updated successfully",
          type: "success",
        })
      );
      close(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "success",
        })
      );
    }
  };

  return (
    <form>
      <BaseModal
        open={open}
        close={() => {
          close(false);
          setSelectedBranch(null);
        }}
        className="-top-[200px] overflow-auto"
        style={{ width: "1000px" }}
      >
        <div className="px-8 py-5">
          <h6 className="text-dark font-medium border-b pb-1">
            {selectedBranch
              ? isEditMode
                ? "Update Branch"
                : "Branch Details"
              : "New Branch"}
          </h6>
        </div>
        <div className="px-8 py-4 grid grid-cols-2 gap-x-6 gap-y-4">
          <BaseTextField
            disabled={isReadOnly}
            label={
              <span>
                Branch Name <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-sm mb-[10px] text-[#505050]"
            >
              Phone <span className="text-red-500">*</span>
            </label>

            <div className="relative w-full">
              <PhoneInput
                disabled={isReadOnly}
                inputClass={`!w-full !h-[42px] !pl-12 !pr-3 !text-sm !font-light !text-black !bg-white !border !rounded-md !shadow-md ${
                  formik.touched.phone &&
                  formik.errors.phone &&
                  "!border-gray-300"
                } focus:!outline-none focus:!ring-1 focus:!ring-orange-400`}
                buttonClass="!border-none !bg-transparent"
                containerClass="!w-full"
                specialLabel=""
                country={"au"}
                value={formik.values.phone}
                onChange={(value) => formik.setFieldValue("phone", value)}
                onBlur={() => formik.setFieldTouched("phone", true)}
                inputProps={{
                  name: "phone",
                  id: "phone",
                  type: "tel",
                }}
              />

              {formik.touched.phone && formik.errors.phone && (
                <FormHelperText error>{formik.errors.phone}</FormHelperText>
              )}
            </div>
          </div>

          <BaseTextField
            disabled={isReadOnly}
            label={
              <span>
                Email <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <BaseTextField
            disabled={isReadOnly}
            labelShrink={false}
            label={
              <span>
                Address <span className="text-red-500">*</span>
              </span>
            }
            optional={false}
            isDefaultField={true}
            required
            fullWidth
            id="addressLine1"
            name="addressLine1"
            value={formik.values.addressLine1}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)
            }
            helperText={
              formik.touched.addressLine1 && formik.errors.addressLine1
            }
          />

          <BaseTextField
            disabled={isReadOnly}
            fullWidth
            fullHeight={false}
            label={
              <span>
                Country <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="country"
            name="country"
            value={formik.values.country || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={formik.touched.country && formik.errors.country}
          />

          <BaseTextField
            disabled={isReadOnly}
            fullWidth
            fullHeight={false}
            label={
              <span>
                State <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="state"
            name="state"
            value={formik.values.state || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.state && Boolean(formik.errors.state)}
            helperText={formik.touched.state && formik.errors.state}
          />
          <BaseTextField
            disabled={isReadOnly}
            fullWidth
            fullHeight={false}
            label={
              <span>
                City <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="city"
            name="city"
            value={formik.values.city || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.city && Boolean(formik.errors.city)}
            helperText={formik.touched.city && formik.errors.city}
          />

          <BaseTextField
            disabled={isReadOnly}
            fullWidth
            fullHeight={false}
            label={
              <span>
                Zip Code <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="zipCode"
            name="zipCode"
            value={formik.values.zipCode || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
            helperText={formik.touched.zipCode && formik.errors.zipCode}
          />

          <BaseAutoSelect
            disabled={isReadOnly}
            labelShrink={false}
            label={
              <span>
                Tax Type <span className="text-red-500">*</span>
              </span>
            }
            required={true}
            fullWidth
            options={[
              {
                label: "GST",
                value: "GST",
              },
              {
                label: "VAT",
                value: "VAT",
              },
            ]}
            id="taxType"
            name="taxType"
            value={formik.values.taxType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.taxType && Boolean(formik.errors.taxType)}
            helperText={formik.touched.taxType && formik.errors.taxType}
          />

          <BaseTextField
            disabled={isReadOnly}
            label={
              <span>
                Tax Number <span className="text-red-500">*</span>
              </span>
            }
            labelShrink={false}
            optional={false}
            id="taxNumber"
            name="taxNumber"
            value={formik.values.taxNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.taxNumber && Boolean(formik.errors.taxNumber)}
            helperText={formik.touched.taxNumber && formik.errors.taxNumber}
          />
        </div>

        <div
          className="d-flex content-center  bg-white py-6 mt-6"
          style={{
            boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
          }}
        >
          <BaseButton
            className=" px-[28px] py-[5px] text-primary hover:!text-white"
            variant="outlined"
            color="primary"
            style={{
              marginRight: "16px",
            }}
            onClick={() => {
              close(false);
              setSelectedBranch(null);
              setIsEditMode?.(false);
            }}
          >
            Cancel
          </BaseButton>

          {!isReadOnly && (
          <BaseButton
            disabled={
              !formik.dirty ||
              !formik.isValid ||
              isCreateCompanyBranchApiLoading ||
              isUpdateBranchApiLoading
            }
            loaderSize="22px"
            isLoading={
              isCreateCompanyBranchApiLoading || isUpdateBranchApiLoading
            }
            color="primary"
            className="py-[5px] px-[20px] min-w-32"
            type="submit"
            onClick={formik.handleSubmit}
          >
            {selectedBranch ? "Update" : "Create"}
          </BaseButton>
          )}
        </div>
      </BaseModal>
    </form>
  );
};

export default AddEditCompanyBranchModal;
